import React, { useEffect, useState } from 'react';
import { Tag, Card, notification, Button, Modal } from 'antd';
import { PrinterOutlined, CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { Lineitem } from '../../../../interfaces/Ordel.Interface';
import { shopDueDate } from '../../../ShopShipDate';

import {
	useStartKittingMutation,
	useCompleteKittingMutation,
	useLazyGetKittingTagsDataQuery,
	usePrintSelectedKittingTagsMutation,
	usePreviewSelectedKittingTagsMutation,
	KittingTagsData,
} from '../../../../redux/api/order-line-item.ts';

import IndividualTagsModal from '../IndividualTagsModal';
import QueueClassTags from '../QueueClassTags';
import { kittingWarnings } from '../kittingWarnings';
import OperatorPinModal from '../../../Operators/OperatorPinModal';
import { usePrintLocation } from '../../../../context/PrintLocationContext';
import {
	getDueDateColor,
	dueDateBorderColor,
} from '../../../../utils/dueDateColor';

const statusTagColor: Record<string, string> = {
	Completed: 'green',
	'In Progress': 'blue',
	'Not Started': 'default',
};

interface IProps {
	item: Lineitem;
	refetch: () => void;
	buildingId?: string;
}

type PinRequest =
	| { kind: 'start-all' }
	| { kind: 'complete' }
	| {
			kind: 'start-selected';
			toolIndices: number[];
			jawIndices: number[];
			otherTagKeys: string[];
	  };

const ItemCard: React.FC<IProps> = ({ item, refetch, buildingId }) => {
	const [startKittingMutation] = useStartKittingMutation();
	const [completeKittingMutation] = useCompleteKittingMutation();
	const [printSelectedMutation] = usePrintSelectedKittingTagsMutation();
	const [previewSelectedMutation] = usePreviewSelectedKittingTagsMutation();
	const [fetchTagsData] = useLazyGetKittingTagsDataQuery();
	const { promptForBuilding } = usePrintLocation();

	const [loadingState, setLoadingState] = useState(false);
	const [choiceModalOpen, setChoiceModalOpen] = useState(false);
	const [individualModalOpen, setIndividualModalOpen] = useState(false);
	const [tagsData, setTagsData] = useState<KittingTagsData | null>(null);
	const [individualPrintLoading, setIndividualPrintLoading] = useState(false);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [pinRequest, setPinRequest] = useState<PinRequest | null>(null);
	const [startUserLogged, setStartUserLogged] = useState(false);

	useEffect(() => {
		setPinRequest(null);
		setStartUserLogged(false);
		setTagsData(null);
		setChoiceModalOpen(false);
		setIndividualModalOpen(false);
		setIndividualPrintLoading(false);
	}, [item.id]);

	const handlePrintClick = () => {
		setChoiceModalOpen(true);
	};

	const runPrintAll = (operatorUserId: number, operatorPin: string) => {
		promptForBuilding(
			async (selectedBuildingId) => {
				setLoadingState(true);
				try {
					await startKittingMutation({
						id: item.id,
						building_id: selectedBuildingId || buildingId,
						operator_user_id: operatorUserId,
						operator_pin: operatorPin,
					}).unwrap();
					setStartUserLogged(true);
					refetch();
					notification.success({
						duration: 10,
						message: 'Print Successful',
						description: 'All tags were sent to the printer.',
						placement: 'topRight',
					});
				} catch (error) {
					console.error('error', error);
					notification.error({
						duration: 10,
						message: 'Error',
						description:
							'Error encountered while attempting to Start the Kitting.',
						placement: 'topRight',
					});
				} finally {
					setLoadingState(false);
				}
			},
			{ description: 'Select the building to print all kitting tags at.' }
		);
	};

	const handlePrintAll = () => {
		setChoiceModalOpen(false);
		setPinRequest({ kind: 'start-all' });
	};

	const handleIndividualChoice = async () => {
		setChoiceModalOpen(false);
		setIndividualPrintLoading(true);
		setIndividualModalOpen(true);
		try {
			const result = await fetchTagsData(item.id).unwrap();
			setTagsData(result);
			setStartUserLogged(Boolean(result.start_kitting_user_logged));
		} catch {
			notification.error({
				message: 'Error',
				description: 'Failed to load tag data.',
				placement: 'topRight',
			});
			setIndividualModalOpen(false);
		} finally {
			setIndividualPrintLoading(false);
		}
	};

	const runPrintSelected = (
		toolIndices: number[],
		jawIndices: number[],
		otherTagKeys: string[],
		operatorUserId?: number,
		operatorPin?: string
	) => {
		promptForBuilding(
			async (selectedBuildingId) => {
				setIndividualPrintLoading(true);
				try {
					const result = await printSelectedMutation({
						id: item.id,
						tool_indices: toolIndices,
						jaw_indices: jawIndices,
						other_tag_keys: otherTagKeys,
						building_id: selectedBuildingId || buildingId,
						...(operatorUserId
							? {
									operator_user_id: operatorUserId,
									operator_pin: operatorPin,
							  }
							: {}),
					}).unwrap();
					if (
						operatorUserId &&
						(toolIndices.length > 0 || jawIndices.length > 0)
					) {
						setStartUserLogged(true);
					}
					refetch();
					setIndividualModalOpen(false);
					const warnings = kittingWarnings(result);
					if (warnings.length > 0) {
						notification.warning({
							duration: 10,
							message: 'Printed with missing labels',
							description: warnings.join(' '),
							placement: 'topRight',
						});
					} else {
						notification.success({
							duration: 10,
							message: 'Print Successful',
							description: `Sent ${
								toolIndices.length + jawIndices.length + otherTagKeys.length
							} tag(s) to the printer.`,
							placement: 'topRight',
						});
					}
				} catch {
					notification.error({
						duration: 10,
						message: 'Error',
						description: 'Error printing selected tags.',
						placement: 'topRight',
					});
				} finally {
					setIndividualPrintLoading(false);
				}
			},
			{ description: 'Select the building to print the selected tags at.' }
		);
	};

	const handlePrintSelected = (
		toolIndices: number[],
		jawIndices: number[],
		otherTagKeys: string[] = []
	) => {
		if (startUserLogged) {
			runPrintSelected(toolIndices, jawIndices, otherTagKeys);
			return;
		}
		setPinRequest({
			kind: 'start-selected',
			toolIndices,
			jawIndices,
			otherTagKeys,
		});
	};

	const handlePreviewSelected = async (
		toolIndices: number[],
		jawIndices: number[],
		otherTagKeys: string[] = []
	) => {
		setPreviewLoading(true);
		try {
			const blob = await previewSelectedMutation({
				id: item.id,
				tool_indices: toolIndices,
				jaw_indices: jawIndices,
				other_tag_keys: otherTagKeys,
			}).unwrap();
			const url = URL.createObjectURL(
				new Blob([blob], { type: 'application/pdf' })
			);
			window.open(url);
		} catch {
			notification.error({
				message: 'Error',
				description: 'Failed to generate preview.',
				placement: 'topRight',
			});
		} finally {
			setPreviewLoading(false);
		}
	};

	const runCompleteKitting = (operatorUserId: number, operatorPin: string) => {
		promptForBuilding(
			async (selectedBuildingId) => {
				setLoadingState(true);
				try {
					const result = await completeKittingMutation({
						id: item.id,
						building_id: selectedBuildingId || buildingId,
						operator_user_id: operatorUserId,
						operator_pin: operatorPin,
					}).unwrap();
					refetch();
					const warnings = kittingWarnings(result);
					if (warnings.length > 0) {
						notification.warning({
							duration: 10,
							message: 'Kitting completed with missing labels',
							description: warnings.join(' '),
							placement: 'topRight',
						});
					} else {
						notification.success({
							duration: 10,
							message: 'Kitting Completed Successfully',
							description:
								'The kitting process has been completed, and your print job was sent to the printer successfully.',
							placement: 'topRight',
						});
					}
				} catch (error) {
					console.error('error', error);
					notification.error({
						duration: 10,
						message: 'Error',
						description: 'Error encountered while attempting to complete.',
						placement: 'topRight',
					});
				} finally {
					setLoadingState(false);
				}
			},
			{
				title: 'Where do you want to print the completion tag?',
				description:
					'The Completed Kitting and Quality Hold tags will be sent to the selected building.',
			}
		);
	};

	const handleCompleteKitting = () => {
		if (item.cart_assembly_status === 'Completed') {
			return;
		}
		setPinRequest({ kind: 'complete' });
	};

	const handlePinConfirm = (userId: number, pin: string) => {
		const request = pinRequest;
		setPinRequest(null);
		if (!request) return;
		if (request.kind === 'start-all') {
			runPrintAll(userId, pin);
			return;
		}
		if (request.kind === 'complete') {
			runCompleteKitting(userId, pin);
			return;
		}
		runPrintSelected(
			request.toolIndices,
			request.jawIndices,
			request.otherTagKeys,
			userId,
			pin
		);
	};

	const pinCopy =
		pinRequest?.kind === 'complete'
			? {
					title: 'Complete Kitting',
					description:
						'Enter your PIN. Your name is logged as the person who completed this kit.',
					label: 'Complete Kitting User',
			  }
			: {
					title: 'Start Kitting',
					description:
						'Enter your PIN. Your name is logged as the person who started this kit.',
					label: 'Start Kitting User',
			  };

	return (
		<>
			<Card
				data-testid="fastlane-kit-card"
				data-kit-id={item.id}
				style={{
					width: '100%',
					height: '100%',
					minWidth: 0,
					borderLeft: `4px solid ${
						dueDateBorderColor[getDueDateColor(shopDueDate(item))]
					}`,
				}}
				styles={{
					body: {
						padding: 16,
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
					},
				}}
			>
				<div
					className="flex flex-col gap-2"
					style={{
						flex: 1,
						minWidth: 0,
						minHeight: 0,
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
					}}
				>
					<div
						className="flex items-center justify-between"
						style={{ marginBottom: 4 }}
					>
						<div style={{ fontSize: 16, color: '#888' }}>
							Due:{' '}
							<strong style={{ color: '#222' }}>
								{shopDueDate(item)
									? dayjs(shopDueDate(item)).format('MM/DD')
									: '—'}
							</strong>
						</div>
						<Tag
							data-testid="fastlane-kit-status"
							style={{ fontSize: 16, marginInlineEnd: 0 }}
							color={statusTagColor[item.cart_assembly_status] ?? 'default'}
						>
							{item.cart_assembly_status}
						</Tag>
					</div>

					<QueueClassTags item={item} />

					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							marginBottom: 8,
							height: 220,
							border: '1px solid #f0f0f0',
							borderRadius: 8,
						}}
					>
						{item.thumbnail_file ? (
							<img
								src={item.thumbnail_file}
								alt="part thumbnail"
								style={{ width: 220, height: 220, objectFit: 'contain' }}
							/>
						) : (
							<span style={{ color: '#999', fontSize: 16 }}>No image</span>
						)}
					</div>

					<div
						style={{
							fontSize: 24,
							fontWeight: 700,
							lineHeight: 1.3,
						}}
					>
						{item.order?.name}
					</div>
					<div style={{ fontSize: 16, color: '#888', minHeight: 30 }}>
						{item.name}
					</div>
					<div style={{ fontSize: 16, minHeight: 22 }}>
						<span style={{ color: '#888' }}>Scheduled:</span>{' '}
						<strong style={{ color: '#222' }}>
							{item.scheduling_start_date
								? dayjs(item.scheduling_start_date).format('MM/DD HH:mm')
								: 'Not scheduled'}
						</strong>
					</div>

					<div
						style={{
							marginTop: 'auto',
							paddingTop: 8,
							width: '100%',
							flexShrink: 0,
						}}
					>
						<div
							className="flex items-baseline"
							style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}
						>
							<span
								data-testid="fastlane-kit-quantity"
								style={{
									fontSize: 34,
									fontWeight: 700,
									lineHeight: 1.1,
								}}
							>
								{item.quantity}
							</span>
							<span style={{ fontSize: 16, color: '#888' }}>
								{item.quantity === 1 ? 'piece' : 'pieces'}
							</span>
						</div>
						<div style={{ display: 'flex', gap: 8 }}>
							<Button
								data-testid="fastlane-kit-print"
								onClick={handlePrintClick}
								style={{ flex: 1, height: 48, fontSize: 16 }}
								loading={loadingState}
								disabled={!item.cam_data_exists}
								icon={<PrinterOutlined />}
							>
								Print
							</Button>
							<Button
								data-testid="fastlane-kit-complete"
								onClick={handleCompleteKitting}
								style={{ flex: 1, height: 48, fontSize: 16 }}
								type="primary"
								loading={loadingState}
								disabled={item.cart_assembly_status === 'Completed'}
								icon={<CheckOutlined />}
							>
								Complete
							</Button>
						</div>
					</div>
				</div>
			</Card>

			{/* Choice Modal: Individual vs All */}
			<Modal
				open={choiceModalOpen}
				onCancel={() => setChoiceModalOpen(false)}
				footer={null}
				centered
				width={420}
			>
				<div style={{ textAlign: 'center', padding: '20px 0' }}>
					<span style={{ fontSize: 18, fontWeight: 500 }}>
						Which tags would you like to print?
					</span>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: 16,
							marginTop: 28,
						}}
					>
						<Button
							data-testid="fastlane-kit-select-tags"
							size="large"
							onClick={handleIndividualChoice}
							style={{ minWidth: 160, height: 52, fontSize: 16 }}
						>
							Select Tags
						</Button>
						<Button
							data-testid="fastlane-kit-print-all"
							size="large"
							type="primary"
							onClick={handlePrintAll}
							style={{ minWidth: 160, height: 52, fontSize: 16 }}
						>
							Print All
						</Button>
					</div>
				</div>
			</Modal>

			{/* Individual Tags Selection Modal */}
			<IndividualTagsModal
				open={individualModalOpen}
				loading={individualPrintLoading}
				data={tagsData}
				onCancel={() => {
					setIndividualModalOpen(false);
					setTagsData(null);
				}}
				onPrint={handlePrintSelected}
				onPreview={handlePreviewSelected}
				previewLoading={previewLoading}
			/>

			<OperatorPinModal
				open={pinRequest !== null}
				title={pinCopy.title}
				description={pinCopy.description}
				label={pinCopy.label}
				onCancel={() => setPinRequest(null)}
				onConfirm={handlePinConfirm}
			/>
		</>
	);
};

export default ItemCard;
